import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { InputMoney } from '@/design_system/InputNumber';
import { InputQuantity } from '@/design_system/InputQuantity/InputQuantity';
import InputText from '@/design_system/InputText';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import IconEdit from '@/icons/Edit.svg';
import IconSparePart from '@/icons/SparePart.svg';
import {
  useDeleteArticleActionSparePart,
  useUpdateArticleActionSparePart,
} from '@/models/articleActionSparePart';
import { ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useDatabaseTranslation } from '@/services/i18n';
import {
  displayQuantity,
  getBasePrice,
  getPriceLabel,
  getShowAmountBeforeTaxes,
} from '@/utils/articleActionSpareParts';
import { createBEMClasses } from '@/utils/classname';

import { SparePartSearchDialog } from '../SparePartSearchDialog';
import { SparePartTypeImage } from '../SparePartTypeImage';

import { ActionSparePartPrice } from './ActionSparePartPrice';
import { ActionSparePartDeleteButton, ActionSparePartRestoreButton } from './ActionSpareParts';

type SparePart = ArticleActionWithRelations['spareParts'][number];

interface BaseSparePartComponentProps {
  actionId: string;
}

interface CurrentSparePartComponentProps extends BaseSparePartComponentProps {
  sparePart: SparePart;
  previousSparePart: SparePart;
}

interface NewSparePartComponentProps extends BaseSparePartComponentProps {
  sparePart: SparePart;
  previousSparePart?: undefined;
}

type SparePartComponentProps = CurrentSparePartComponentProps | NewSparePartComponentProps;

const { element } = createBEMClasses('action-spare-parts');

const getSpareParts = (
  action?: ArticleActionWithRelations,
  previousAction?: ArticleActionWithRelations
): SparePart[] => {
  const currentSpareParts = action ? action.spareParts : [];

  const deletedSpareParts = previousAction
    ? previousAction.spareParts.filter(
        (previousSparePart) =>
          !currentSpareParts.some((sparePart) => sparePart.stableId === previousSparePart.stableId)
      )
    : [];

  return [...deletedSpareParts, ...currentSpareParts];
};

const getCurrentAndPreviousSparePart = (
  sparePart: SparePart,
  action?: ArticleActionWithRelations,
  previousAction?: ArticleActionWithRelations
): {
  previousSparePart?: SparePart;
  currentSparePart?: SparePart;
} => {
  const previousSparePart = previousAction?.spareParts.find(
    (sparePartSnapshot) => sparePartSnapshot.stableId == sparePart.stableId
  );

  const currentSparePart = action?.spareParts.find(
    (currentSparePart) => currentSparePart.stableId === sparePart.stableId
  );

  return { previousSparePart, currentSparePart };
};

export const ActionSparePartsMobileBadges = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();
  const { _db } = useDatabaseTranslation();
  const actionId = action ? action.id : previousAction!.id;

  const spareParts = getSpareParts(action, previousAction);
  const showWorkshopPrice = requestView.workshopPrice.shown;
  const showOrganizationPrice = requestView.organizationPrice.shown;

  return spareParts.map((sparePart: SparePart) => {
    const { currentSparePart, previousSparePart } = getCurrentAndPreviousSparePart(
      sparePart,
      action,
      previousAction
    );

    return (
      <Badge
        className={element('badge', {
          deleted: !currentSparePart && previousSparePart,
        })}
        key={sparePart.id}
        hasBorder
        color={currentSparePart && !previousSparePart && previousAction ? 'tertiary' : 'neutral'}
        borderRadius="soft"
        size="large"
      >
        <IconSparePart />
        <Stack>
          {_db(sparePart.sparePartType.name)} {displayQuantity(sparePart, _db)}{' '}
          {!!sparePart.referenceNumber && `- ${sparePart.referenceNumber}`}
          {view.services.actions.fields.spareParts.prices.shown && (
            <>
              {showWorkshopPrice && (
                <ActionSparePartPrice
                  disabled={!currentSparePart}
                  sparePart={sparePart}
                  priceType="workshop"
                  actionId={actionId}
                  textSize="small"
                />
              )}
              {showOrganizationPrice && (
                <ActionSparePartPrice
                  disabled={!currentSparePart}
                  sparePart={sparePart}
                  priceType="organization"
                  actionId={actionId}
                  textSize="small"
                />
              )}
            </>
          )}
        </Stack>
      </Badge>
    );
  });
};

export const ActionSparePartsMobileList = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const [showAddSparePart, setShowAddSparePart] = useState(false);
  const { view } = useArticleContext();
  const showAddButton = view.services.actions.fields.spareParts.canAddRemoveSparePart;

  const spareParts = getSpareParts(action, previousAction);

  return (
    <Stack gap="0.5rem">
      <span className="label-100-regular">
        <Trans id="article.form.actions.spare-parts.label">Spare parts</Trans>
      </span>
      {!!spareParts.length && (
        <Stack gap="0.25rem">
          {spareParts.map((sparePart) => (
            <ActionSparePartMobileCard
              key={sparePart.id}
              sparePart={sparePart}
              action={action}
              previousAction={previousAction}
            />
          ))}
        </Stack>
      )}
      {action && (
        <>
          {showAddButton && (
            <Button
              variant="secondary"
              size="small"
              style={{ width: 'fit-content', padding: '8px' }}
              onPress={() => setShowAddSparePart(true)}
            >
              <Stack row alignItems="center" gap="4px">
                <IconAdd />
                <InlineText size="200" weight="medium">
                  <Trans id="spare-parts-table.add_spare_part.button.label">Add a spare part</Trans>
                </InlineText>
              </Stack>
            </Button>
          )}
          <CreateSparePartMobileDialog
            isOpen={showAddSparePart}
            setIsOpen={setShowAddSparePart}
            action={action}
          />
        </>
      )}
    </Stack>
  );
};

const ActionSparePartMobileCard = ({
  sparePart,
  action,
  previousAction,
}: {
  sparePart: SparePart;
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { view } = useArticleContext();

  const { currentSparePart, previousSparePart } = getCurrentAndPreviousSparePart(
    sparePart,
    action,
    previousAction
  );

  const showEditButton =
    action &&
    currentSparePart &&
    (view.services.actions.fields.spareParts.quantity.editable ||
      view.services.actions.fields.spareParts.referenceNumber.editable);
  const showDeleteButton =
    action && currentSparePart && view.services.actions.fields.spareParts.canAddRemoveSparePart;

  const currentActionAlreadyHasSparePartType = action?.spareParts.some(
    (sparePartInCurrentSnapshot) =>
      sparePartInCurrentSnapshot.sparePartTypeId === sparePart.sparePartTypeId
  );
  const showRestoreButton =
    action &&
    !currentSparePart &&
    view.services.actions.fields.spareParts.canAddRemoveSparePart &&
    !currentActionAlreadyHasSparePartType;

  return (
    <Box
      className={element('card', {
        new: !previousSparePart && previousAction,
        deleted: !currentSparePart,
      })}
    >
      <Stack row justifyContent="space-between" alignItems="center" flexWrap="nowrap">
        <ActionSparePartInfos
          showPrices={true}
          sparePart={sparePart}
          isDeleted={!currentSparePart}
          actionId={action ? action.id : previousAction!.id}
        />
        <Stack row gap="0.5rem" flexWrap="nowrap">
          {showEditButton && (
            <Button variant="secondary" onPress={() => setIsDialogOpen(true)} iconOnly>
              <IconEdit />
            </Button>
          )}
          {showDeleteButton && (
            <ActionSparePartDeleteButton sparePart={sparePart} actionId={action.id} />
          )}
          {showRestoreButton && (
            <ActionSparePartRestoreButton sparePart={sparePart} actionId={action.id} />
          )}
        </Stack>
      </Stack>
      {action && (
        <ActionSparePartMobileEditDialog
          actionId={action.id}
          isOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          mode="edit"
          sparePart={sparePart}
          previousSparePart={previousSparePart}
        />
      )}
    </Box>
  );
};

const ActionSparePartInfos = ({
  sparePart,
  isDeleted,
  showPrices,
  actionId,
}: {
  sparePart: SparePart;
  isDeleted: boolean;
  showPrices: boolean;
  actionId: string;
}) => {
  const { _db } = useDatabaseTranslation();
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();
  const sparePartName = _db(sparePart.sparePartType.name);
  const showWorkshopPrice = requestView.workshopPrice.shown;
  const showOrganizationPrice = requestView.organizationPrice.shown;

  return (
    <Stack row alignItems="center" gap="8px" flexWrap="nowrap">
      <SparePartTypeImage sparePartType={sparePart.sparePartType} />
      <Stack gap="4px">
        <InlineText>
          {sparePartName} {displayQuantity(sparePart, _db)}{' '}
          {!!sparePart.referenceNumber && `- ${sparePart.referenceNumber}`}
        </InlineText>
        {showPrices && view.services.actions.fields.spareParts.prices.shown && (
          <>
            {showWorkshopPrice && (
              <ActionSparePartPrice
                disabled={isDeleted}
                sparePart={sparePart}
                priceType="workshop"
                actionId={actionId}
              />
            )}
            {showOrganizationPrice && (
              <ActionSparePartPrice
                disabled={isDeleted}
                sparePart={sparePart}
                priceType="organization"
                actionId={actionId}
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

const ActionSparePartMobileEditDialog = ({
  isOpen,
  setIsOpen,
  sparePart,
  actionId,
  mode,
  previousSparePart,
}: {
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  sparePart?: SparePart;
  actionId: string;
  mode: 'create' | 'edit';
  previousSparePart?: SparePart;
}) => {
  const { article, request } = useArticleContext();
  const { t } = useLingui();

  const title =
    mode === 'edit'
      ? t({ id: 'edit-spare-part.dialog.title.edit', message: 'Edit spare part' })
      : t({ id: 'edit-spare-part.dialog.title', message: 'Add spare part' });

  const { mutate: deleteSparePart } = useDeleteArticleActionSparePart({
    articleId: article.id,
    requestId: request.id,
    actionId,
  });

  return (
    <Dialog
      title={title}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        setIsOpen(newIsOpen);

        if (!newIsOpen && mode === 'create' && !!sparePart) {
          deleteSparePart(sparePart.id);
        }
      }}
    >
      {!!sparePart && (
        <ActionSparePartMobileEditForm
          onSave={() => setIsOpen(false)}
          actionId={actionId}
          sparePart={sparePart}
          previousSparePart={previousSparePart}
        />
      )}
    </Dialog>
  );
};

const ActionSparePartMobileEditForm = ({
  onSave,
  actionId,
  sparePart,
  previousSparePart,
}: {
  onSave: () => void;
  actionId: string;
  sparePart: SparePart;
  previousSparePart?: SparePart;
}) => {
  const { view } = useArticleContext();
  const { view: requestView } = useRequestContext();
  const showEditQuantity = view.services.actions.fields.spareParts.quantity.editable;
  const showEditReferenceNumber = view.services.actions.fields.spareParts.referenceNumber.editable;
  const showEditWorkshopPrice =
    requestView.workshopPrice.shown &&
    view.services.actions.fields.spareParts.workshopPrice.editable;
  const showEditOrganizationPrice =
    requestView.organizationPrice.shown &&
    view.services.actions.fields.spareParts.organizationPrice.editable;

  return (
    <>
      <main>
        <Stack gap="1rem">
          <ActionSparePartInfos
            isDeleted={false}
            showPrices={false}
            sparePart={sparePart}
            actionId={actionId}
          />
          {showEditReferenceNumber && (
            <ActionSparePartMobileReferenceNumberInput
              actionId={actionId}
              sparePart={sparePart}
              previousSparePart={previousSparePart}
            />
          )}
          {showEditQuantity && (
            <ActionSparePartMobileQuantityInput
              actionId={actionId}
              sparePart={sparePart}
              previousSparePart={previousSparePart}
            />
          )}
          {showEditWorkshopPrice && (
            <ActionSparePartMobilePriceInput
              actionId={actionId}
              sparePart={sparePart}
              priceType={'workshop'}
            />
          )}
          {showEditOrganizationPrice && (
            <ActionSparePartMobilePriceInput
              actionId={actionId}
              sparePart={sparePart}
              priceType={'organization'}
            />
          )}
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button variant="primary" onPress={onSave}>
            <Trans id="edit-spare-part.dialog.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

const ActionSparePartMobileReferenceNumberInput = ({
  actionId,
  sparePart,
  previousSparePart,
}: SparePartComponentProps) => {
  const { t } = useLingui();
  const { request, article } = useArticleContext();
  const [value, setValue] = useState(sparePart.referenceNumber || undefined);

  const { mutateAsync: updateSparePart } = useUpdateArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId: actionId,
    sparePartId: sparePart.id,
  });

  const debouncedUpdateSparePart = useMemo(() => {
    return debounce(updateSparePart, 500);
  }, [updateSparePart]);

  const hasBeenEdited =
    previousSparePart && previousSparePart.referenceNumber !== sparePart.referenceNumber;

  return (
    <InputText
      edited={hasBeenEdited}
      label={
        <>
          <Trans id="edit-spare-part.dialog.referenceNumber">Reference number</Trans>
          <OptionalLabel />
        </>
      }
      labelFontWeight="regular"
      placeholder={t({
        id: 'edit-spare-part.dialog.referenceNumber.placeholder',
        message: 'Add a reference number',
      })}
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        debouncedUpdateSparePart({
          referenceNumber: newValue,
        });
      }}
    />
  );
};

const ActionSparePartMobileQuantityInput = ({
  actionId,
  sparePart,
  previousSparePart,
}: SparePartComponentProps) => {
  const { t } = useLingui();
  const { request, article, errors } = useArticleContext();
  const [value, setValue] = useState(sparePart.quantity);

  const { mutateAsync: updateSparePart } = useUpdateArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId: actionId,
    sparePartId: sparePart.id,
  });

  const hasError = !!errors.services?.actions?.actions
    ?.find((act) => act.id === actionId)
    ?.spareParts?.find((sparePartError) => sparePartError.id === sparePart.id)?.quantityIsNull;

  const debouncedUpdateSparePart = useMemo(() => {
    return debounce(updateSparePart, 500);
  }, [updateSparePart]);

  const hasBeenEdited =
    previousSparePart && sparePart && previousSparePart.quantity !== sparePart.quantity;

  return (
    <Stack gap="0.5rem">
      <span className="label-100-regular">
        <Trans id="edit-spare-part.dialog.quantity.label">Quantity</Trans>
      </span>
      <InputQuantity
        edited={hasBeenEdited}
        isInvalid={hasError}
        minValue={1}
        ariaLabel={t({
          id: 'edit-spare-part.dialog.quantity.aria-label',
          message: 'Quantity of spare parts',
        })}
        onChange={(newValue) => {
          setValue(newValue);
          debouncedUpdateSparePart({
            quantity: newValue,
          });
        }}
        value={value}
        variant="expanded"
        size="large"
      />
    </Stack>
  );
};

const ActionSparePartMobilePriceInput = ({
  actionId,
  sparePart,
  priceType,
}: {
  actionId: string;
  sparePart: SparePart;
  priceType: 'workshop' | 'organization';
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { request, article } = useArticleContext();
  const { view: requestView } = useRequestContext();
  const price = getBasePrice(priceType, sparePart);
  const showAmountBeforeTaxes = getShowAmountBeforeTaxes(priceType, requestView);
  const priceLabel = getPriceLabel(priceType, requestView);

  const amount = showAmountBeforeTaxes ? price?.amountBeforeTaxes : price?.amount;

  const [value, setValue] = useState(amount ?? undefined);
  const unit = sparePart.sparePartType.unit
    ? _db(sparePart.sparePartType.unit)
    : t({
        id: 'spare-parts-table.column.reference.part',
        message: 'part',
      });

  const perPartTranslation = t({
    id: 'edit-spare-part.dialog.price.per-part',
    message: `per ${unit}`,
  });
  const label = `${priceLabel} (${perPartTranslation})`;

  const { mutateAsync: updateSparePart } = useUpdateArticleActionSparePart({
    requestId: request.id,
    articleId: article.id,
    actionId: actionId,
    sparePartId: sparePart.id,
  });

  const debouncedUpdateSparePart = useMemo(() => {
    return debounce(updateSparePart, 500);
  }, [updateSparePart]);

  const updateField = priceType === 'workshop' ? 'customWorkshopPrice' : 'customOrganizationPrice';
  const updateAmountField = showAmountBeforeTaxes ? 'amountBeforeTaxes' : 'amount';

  return (
    !!price && (
      <InputMoney
        size="large"
        label={label}
        value={value}
        currency={price.currency}
        onChange={(newValue) => {
          setValue(newValue);

          debouncedUpdateSparePart({
            [updateField]:
              newValue !== undefined && !isNaN(newValue)
                ? {
                    [updateAmountField]: newValue,
                  }
                : null,
          });
        }}
      />
    )
  );
};

const CreateSparePartMobileDialog = ({
  isOpen,
  setIsOpen,
  action,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  action: ArticleActionWithRelations;
}) => {
  const [createdSparePartId, setCreatedSparePartId] = useState<string>();

  const createdSparePart = action.spareParts.find((d) => d.id === createdSparePartId);

  return (
    <>
      <SparePartSearchDialog
        actionId={action.id}
        isOpen={isOpen && !createdSparePart}
        onClose={() => setIsOpen(false)}
        onSparePartCreated={setCreatedSparePartId}
      />
      <ActionSparePartMobileEditDialog
        isOpen={isOpen && !!createdSparePart}
        setIsOpen={(newIsOpen) => {
          setIsOpen(newIsOpen);

          if (!newIsOpen) {
            setCreatedSparePartId(undefined);
          }
        }}
        sparePart={createdSparePart}
        actionId={action.id}
        mode="create"
      />
    </>
  );
};
